// `<PasswordField>` — the show/hide password input shared by the auth pages
// (LoginPage, SignUpPage, ResetPasswordPage). Extracts the byte-identical
// `<Input type={…} iconRight={<IconButton …Eye/EyeOff…/>} />` block that was
// copy-pasted three times.
//
// `forwardRef` matters: callers spread `{...register('password')}` directly
// onto this component, and react-hook-form's `ref` must reach the underlying
// DOM input. Forwarding it keeps the call sites identical to the inline markup.
//
// Visibility is uncontrolled by default (own `useState`, as Login/SignUp had).
// ResetPasswordPage drives two inputs from a single toggle, so it passes the
// controlled `shown` + `onToggleShown` pair to keep both fields in lockstep.

import { forwardRef, useState, type ComponentProps } from 'react';

import { IconButton, Input } from '@barel/ui';
import { Eye, EyeOff } from 'lucide-react';

export interface PasswordFieldProps extends Omit<
  ComponentProps<typeof Input>,
  'type' | 'iconRight'
> {
  /** aria-label for the toggle while the password is hidden (click reveals). */
  toggleShowLabel: string;
  /** aria-label for the toggle while the password is shown (click hides). */
  toggleHideLabel: string;
  /** Optional test id for the toggle button. */
  toggleTestId?: string;
  /** Controlled visibility; omit to let the field manage its own state. */
  shown?: boolean;
  /** Toggle handler — required when `shown` is provided. */
  onToggleShown?: () => void;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  function PasswordField(
    { toggleShowLabel, toggleHideLabel, toggleTestId, shown, onToggleShown, ...inputProps },
    ref,
  ) {
    const [internalShown, setInternalShown] = useState(false);
    const isControlled = shown !== undefined;
    const show = isControlled ? shown : internalShown;
    const toggle = () => {
      if (isControlled) onToggleShown?.();
      else setInternalShown((v) => !v);
    };

    return (
      <Input
        {...inputProps}
        ref={ref}
        type={show ? 'text' : 'password'}
        iconRight={
          <IconButton
            type="button"
            variant="ghost"
            size="sm"
            aria-label={show ? toggleHideLabel : toggleShowLabel}
            onClick={toggle}
            icon={show ? <EyeOff size={14} /> : <Eye size={14} />}
            data-testid={toggleTestId}
          />
        }
      />
    );
  },
);
